import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowRight, Target, Rocket, Users, Shield, Zap, Globe, TrendingUp, Sparkles
} from 'lucide-react';

function AboutPage() {
  const navigate = useNavigate();

  const values = [
    {
      icon: <Target className="w-10 h-10" />,
      title: "Our Mission",
      description: "To help enterprises evolve their systems and processes into modern, scalable platforms while preserving business continuity and maximizing value through technology innovation."
    },
    {
      icon: <Rocket className="w-10 h-10" />,
      title: "Our Vision",
      description: "To be the trusted partner for organizations navigating digital transformation—delivering solutions that are reliable, innovative, and aligned with business goals."
    }
  ];

  const leadershipTeam = [
    {
      name: "Suman Chimata",
      role: "CEO & Founder",
      bio: "Bringing over two decades of expertise in mainframe strategy, modernization, and delivery across SAP, Java, and AI applications alongside."
    },
    {
      name: "Radhika Challa",
      role: "Chief Technology Officer",
      bio: "Whose portfolio spans enterprise architecture, microservices, advanced Java engineering, SAP integration, and AI-driven solutions."
    }
  ];

  const capabilities = [
    { icon: <Shield />, text: "20+ Years Industry Expertise" },
    { icon: <Zap />, text: "Cutting-Edge Technology Stack" },
    { icon: <Users />, text: "End-to-End Solution Delivery" },
    { icon: <Globe />, text: "Global Standards & Compliance" },
    { icon: <TrendingUp />, text: "Proven Track Record" },
    { icon: <Sparkles />, text: "Innovation-Driven Approach" }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="hero-section" style={{
        backgroundImage: 'linear-gradient(135deg, rgba(10, 126, 164, 0.7) 0%, rgba(6, 90, 117, 0.7) 100%)',
        minHeight: '50vh'
      }}>
        <div className="hero-content">
          <div className="hero-badge">
            <Users className="w-4 h-4" />
            <span>About PrimeFields</span>
          </div>
          <h1 className="hero-title">Building the Future<br />of Enterprise Technology</h1>
          <p className="hero-subtitle">
            Transforming businesses through innovative technology solutions across multiple industries
          </p>
        </div>
      </section>

      {/* Mission, Vision, Leadership */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">Who We Are</Badge>
            <h2 className="section-title">Bridging Legacy and Innovation</h2>
            <p className="section-description">
              We combine deep industry expertise with modern technology to deliver transformative solutions
            </p>
          </div>

          <div className="about-grid">
            {values.map((value, index) => (
              <Card key={index} className="about-card">
                <CardHeader>
                  <div className="card-icon" style={{ color: 'var(--accent)' }}>
                    {value.icon}
                  </div>
                  <CardTitle>{value.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p>{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">Leadership</Badge>
            <h2 className="section-title">Our Leadership Team</h2>
            <p className="section-description">
              PrimeFields’ leadership team drives enterprise innovation and accelerates digital transformation.
            </p>
          </div>

          <div className="about-grid">
            {leadershipTeam.map((leader, index) => (
              <Card key={index} className="about-card">
                <CardHeader className="text-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    background: 'var(--bg-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1.5rem',
                    border: '2px solid var(--border-glow)'
                  }}>
                    <Users className="w-12 h-12" style={{ color: 'var(--accent)' }} />
                  </div>
                  <CardTitle style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{leader.name}</CardTitle>
                  <Badge variant="secondary" style={{
                    background: 'rgba(34, 211, 238, 0.1)',
                    color: 'var(--accent)',
                    border: '1px solid rgba(34, 211, 238, 0.2)',
                    marginBottom: '1rem'
                  }}>
                    {leader.role}
                  </Badge>
                </CardHeader>
                <CardContent style={{ textAlign: 'center' }}>
                  <p>{leader.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">What We Do</Badge>
            <h2 className="section-title">Comprehensive Technology Solutions</h2>
          </div>

          <div className="services-overview-grid">
            <Card className="service-overview-card">
              <CardHeader>
                <CardTitle>Legacy Modernization</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Transform mainframe and legacy systems into modern, cloud-native platforms with proven methodologies and zero-downtime strategies.</p>
                <Button variant="link" onClick={() => navigate('/products/legacy-modernization')} style={{ padding: 0, marginTop: '1rem' }}>
                  Learn More <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </CardContent>
            </Card>

            <Card className="service-overview-card">
              <CardHeader>
                <CardTitle>Healthcare Solutions</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Modern HIS/EMR systems and smart health kiosks that make healthcare accessible, intelligent, and effortless.</p>
                <Button variant="link" onClick={() => navigate('/products/healthcare')} style={{ padding: 0, marginTop: '1rem' }}>
                  Learn More <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </CardContent>
            </Card>

            <Card className="service-overview-card">
              <CardHeader>
                <CardTitle>Travel CRM</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Complete travel business management platform from lead capture to post-trip feedback automation.</p>
                <Button variant="link" onClick={() => navigate('/products/triptrust')} style={{ padding: 0, marginTop: '1rem' }}>
                  Learn More <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">Why Choose Us</Badge>
            <h2 className="section-title">Built on Expertise & Innovation</h2>
          </div>

          <div className="capabilities-grid">
            {capabilities.map((capability, index) => (
              <div key={index} className="capability-item">
                <div className="capability-icon">{capability.icon}</div>
                <p className="capability-text">{capability.text}</p>
              </div>
            ))}
          </div>

          <div className="about-cta" style={{ textAlign: 'center', marginTop: '3rem' }}>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--text-primary)' }}>
              Ready to Transform Your Business?
            </h3>
            <Button
              size="lg"
              className="cta-primary"
              onClick={() => navigate('/contact')}
            >
              Get In Touch <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;
